import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const TIPS = [
  {
    title: "Shoot in natural daylight",
    description:
      "Natural light is the single biggest factor in property photography. Shoot between 10am and 2pm on a bright day, with all curtains and blinds fully open. Turn on every light in the property to fill shadows and create warmth. Avoid shooting towards windows; instead, position yourself with the light source behind or beside you.",
  },
  {
    title: "Declutter and stage every room",
    description:
      "Remove personal items, excess furniture, and anything that distracts from the space itself. A clean, uncluttered room photographs larger and more inviting. Add simple staging touches: fresh flowers on the kitchen counter, neatly folded towels in the bathroom, and a throw on the sofa. The goal is to help buyers imagine their own life in the property.",
  },
  {
    title: "Use a wide-angle lens (but not too wide)",
    description:
      "A wide-angle lens (10-16mm on a crop sensor, 16-24mm on full frame) captures more of each room and makes spaces feel larger. However, ultra-wide lenses distort proportions and make rooms look unrealistic. Buyers feel misled when the property looks smaller in person. Aim for a natural-looking wide angle that represents the space honestly.",
  },
  {
    title: "Shoot from corners at chest height",
    description:
      "Position yourself in the corner of the room at roughly chest height (around 1.2 metres). This angle captures the maximum floor area and gives a natural, balanced perspective. Avoid shooting from doorways, which often crop the room awkwardly, and avoid eye-level shots, which can make ceilings feel low.",
  },
  {
    title: "Get the exterior right",
    description:
      "The exterior shot is the first image buyers see on the portal listing. Mow the lawn, clear the driveway, and remove bins and clutter. Shoot on a bright day with the sun behind you to avoid harsh shadows on the facade. Include some sky and context (street, garden) to help buyers understand the setting.",
  },
  {
    title: "Photograph in the right order",
    description:
      "Shoot rooms in the order a buyer would walk through the property: exterior, entrance hall, living room, kitchen, dining room, bedrooms, bathrooms, garden. This creates a logical visual flow for the listing. Take 8-15 photos for a standard property, ensuring each room is represented with its best angle.",
  },
  {
    title: "Edit consistently but honestly",
    description:
      "Post-processing should correct exposure, white balance, and lens distortion. Straighten verticals, brighten shadows, and ensure consistent colour temperature across all images. Do not over-saturate colours, add artificial skies, or digitally remove permanent features. Buyers expect the property to match the photos; misleading images erode trust and waste viewing time.",
  },
  {
    title: "Do not forget the details",
    description:
      "Close-up shots of architectural features, original fireplaces, designer kitchens, and garden views add depth to a listing. These detail shots work particularly well in social media carousels and video content. They also signal quality to buyers browsing quickly through portal listings.",
  },
]

const MISTAKES = [
  "Shooting with a smartphone in portrait orientation",
  "Leaving toilet lids up in bathroom photos",
  "Including reflections (mirrors, windows) showing the photographer",
  "Photographing in poor light or with mixed lighting (warm and cool)",
  "Leaving bins, washing up, or laundry visible in shots",
  "Using a fish-eye lens that makes rooms look unrealistically large",
  "Forgetting to photograph the garden, parking, or exterior views",
  "Uploading photos in the wrong order on portal listings",
]

const FAQ = [
  {
    q: "Should I hire a professional photographer or do it myself?",
    a: "For most agents, hiring a professional photographer is worth the investment. A professional shoot typically costs \u00a3100-250 per property and delivers consistently high-quality results. If budget is tight or you photograph multiple properties per week, investing in a good camera and training can pay off over time. Either way, never rely on smartphone photos for portal listings.",
  },
  {
    q: "What equipment do I need for property photography?",
    a: "At minimum: a camera with manual controls (even a recent mirrorless body), a wide-angle lens (16-24mm full frame equivalent), and a sturdy tripod. A remote shutter release and an external flash for bouncing light off ceilings are useful additions. Many agents achieve excellent results with a camera kit costing under \u00a31,000.",
  },
  {
    q: "How many photos should a property listing have?",
    a: "For a standard 2-3 bedroom house, aim for 10-15 photos covering every room, the exterior, and the garden. For larger properties, 15-25 photos is appropriate. Always include a floorplan. Quality matters more than quantity; five excellent photos outperform twenty mediocre ones.",
  },
  {
    q: "Do I need drone photography?",
    a: "Drone photography is valuable for larger properties, rural settings, and developments where aerial views show the property\u2019s context and surroundings. For standard terraced or semi-detached houses, it is usually unnecessary. If you do use a drone, ensure the operator holds a valid CAA flyer ID and operator ID.",
  },
  {
    q: "How does photography feed into video marketing?",
    a: "Strong photography is the foundation of effective video marketing. Tools like QuiteRight use your listing photos to create professional video ads automatically, so the quality of your photos directly affects the quality of your videos. Better photos mean better videos, more enquiries, and faster sales.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function PropertyPhotographyTipsGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Property Photography Tips for Estate Agents",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Property Photography Tips" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Property photography tips
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            How to photograph properties that sell. Practical tips on
            lighting, composition, staging, and editing for estate agents
            and property photographers.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why photography makes or breaks a listing
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The first photo on your portal listing is a five-second
              audition. Buyers scrolling through Rightmove or Zoopla will
              either click through or keep scrolling based entirely on that
              single image. Properties with professional photography
              receive significantly more clicks, more viewings, and
              stronger offers.
            </p>
            <p>
              Yet a surprising number of agents still upload dark,
              cluttered smartphone photos that actively harm their
              listings. In a market where 95% of buyers start their search
              online, photography is not a nice-to-have; it is the most
              fundamental marketing asset you produce.
            </p>
            <p>
              This guide covers everything you need to produce
              professional-quality property photos, whether you shoot
              in-house or brief a photographer.
            </p>
          </div>
        </div>
      </section>

      {/* Tips */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Eight essential photography tips
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Follow these techniques for consistently better property
            photos on every listing.
          </p>

          <div className="mt-8 space-y-6">
            {TIPS.map((tip, index) => (
              <ScrollReveal key={tip.title}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <div className="flex items-start gap-4">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red/10 font-display text-sm font-bold text-red">
                      {index + 1}
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold text-charcoal">
                        {tip.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate">
                        {tip.description}
                      </p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* DIY vs pro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            DIY vs hiring a professional
            <span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Hire a professional when:
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate">
                {[
                  "The property value justifies the investment (\u00a3300k+)",
                  "You need consistency across a large portfolio",
                  "The property has challenging lighting conditions",
                  "You want twilight or drone photography",
                  "You need the images for print brochures",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="mt-0.5 size-4 shrink-0 text-red"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-stone bg-white p-5">
              <h3 className="font-display text-base font-bold text-charcoal">
                Shoot in-house when:
              </h3>
              <ul className="mt-3 space-y-2 text-sm text-slate">
                {[
                  "You photograph multiple properties per week",
                  "Turnaround speed is critical (same-day listing)",
                  "You have invested in proper equipment and training",
                  "The property is straightforward (good light, tidy)",
                  "Budget constraints apply across a high volume of listings",
                ].map((item) => (
                  <li key={item} className="flex gap-2">
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="mt-0.5 size-4 shrink-0 text-slate"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Common mistakes */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Common mistakes to avoid
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {MISTAKES.map((mistake) => (
              <div
                key={mistake}
                className="flex items-start gap-2 rounded-lg border border-stone bg-white px-4 py-3"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="mt-0.5 size-4 shrink-0 text-red"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-charcoal">{mistake}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photos to video */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            From photos to video
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Great photography is the starting point, not the finish line.
              The same images that make your portal listings shine can be
              transformed into scroll-stopping video ads for social media.
            </p>
            <p>
              Tools like{" "}
              <Link
                href="/features"
                className="text-red underline decoration-red/30 transition-colors hover:text-deep-red"
              >
                QuiteRight
              </Link>{" "}
              automatically create professional property videos from your
              listing photos in under 60 seconds. Better photos feed
              directly into better videos, which generate more enquiries
              and help you win more instructions.
            </p>
            <p>
              This is why photography quality matters beyond the portal
              listing: every marketing channel you use downstream, from
              Instagram Reels to YouTube Shorts to vendor reports, depends
              on the quality of your original images.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "95%", label: "of buyers start their search online" },
            { stat: "2x", label: "more enquiries with professional photos" },
            { stat: "32%", label: "more clicks on listings with 10+ photos" },
            { stat: "<60s", label: "to turn photos into video with QuiteRight" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "The full guide to video types, platforms, and best practices.",
              },
              {
                href: "/guides/property-marketing-checklist",
                title: "Marketing checklist",
                desc: "The complete 24-point property marketing checklist.",
              },
              {
                href: "/examples",
                title: "Video examples",
                desc: "See property videos created from listing photos.",
              },
              {
                href: "/guides/marketing-rental-property",
                title: "Marketing rental properties",
                desc: "Photography and video tips for letting agents.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Turn great photos into great videos
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Your listing photos are already doing the hard work. QuiteRight
            turns them into scroll-stopping video ads in under 60 seconds.
            More viewings, faster sales, no extra effort.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
